/**
 * Per-dimension educational content shown in the inline info popovers
 * (DimensionInfoButton) on filter sheets, the review panel, and the
 * story-page themes block.
 *
 * Each entry mirrors the FILTER_DIMENSIONS key. Content is intentionally
 * compact — a few sentences a reader can absorb mid-task without
 * leaving the page. Deeper material lives on the standalone /learn route
 * (planned follow-up).
 *
 * Style guide:
 *   - Lead with what the reader will recognize, not the Sanskrit term.
 *   - Sanskrit names go in `sanskrit` field (small-caps in the UI).
 *   - `summary` is one to two short paragraphs, plain English.
 *   - `examples` is an optional list of short pairs to ground the abstract.
 *   - Avoid "should" / prescriptions — this is a map, not a manual.
 */

export const DIMENSION_EDU = {
  professions: {
    title: 'Profession',
    sanskrit: 'Karma — the work life puts in your hands',
    summary:
      'Who the author is in everyday life. Profession shapes what you see, what you wrestle with, and what wisdom your readers can carry into their own work. Use this to find stories from people whose days look like yours.',
    examples: [
      '🩺 Doctor / Healthcare — burnout, presence, holding others',
      '🎓 Student — discipline, doubt, finding direction',
      '🏠 Homemaker — invisible work, devotion, daily care',
      '🧘 Classical Hatha Yoga Teacher — embodiment, lineage, teaching',
    ],
    paragraphs: [
      "Most tagging on Katha Mithra is about the inner life — what shifted, what got stuck, what energy carried the journey. Profession is different. It's a marker of where the author stands in the outer world: the work that fills their days, the people they sit beside, the constraints they live inside.",
      "A doctor's story about exhaustion and presence reads differently to another doctor than to a poet. Not better or worse — different. Profession is optional and lives separately from the story's themes, so the inner taxonomy stays clean while readers can still find peers.",
    ],
    how_we_use:
      "Pick all that fit — many people wear multiple hats. Leave blank if none feel right.",
  },

  life_areas: {
    title: 'Life area',
    sanskrit: 'Purushartha — domains of human life',
    summary:
      'Where in your life this story plays out. Health, work, relationships, community, inner peace, spiritual growth — every transformation lives somewhere in your everyday life, even when it ripples beyond.',
    examples: [
      'Body & wellbeing — how you eat, move, sleep',
      'Relationships & love — partnership, family, friends',
      'Career & contribution — work, service, impact',
      'Inner peace — calm, anxiety, meditation',
      'Spiritual growth — meaning, transcendence',
    ],
    paragraphs: [
      "The Vedic frame for human life recognized that we don't live in just one domain — we live across many. Purushartha names four basic pursuits (dharma, artha, kama, moksha), and within each, life keeps showing up in different rooms: the body you eat from, the work you do, the people you sit with, the quiet you can or can't find.",
      "On Katha Mithra, life areas are the where of a story. A story about caring for an aging parent and a story about finishing a degree both involve transformation — but the texture, the language, and the readers who'll find them most useful are different. Tagging the area helps the right reader meet your story.",
    ],
    how_we_use:
      "A story can sit in one or two areas. We pick what's central, not everything that's mentioned in passing.",
  },

  guna_arc: {
    title: 'Energy arc',
    sanskrit: 'Trigunas — the three forces of mind',
    summary:
      'The mind moves through three energies: heavy & stuck, restless & busy, calm & clear. Most journeys are an arc through them — you might start stuck, get agitated as something shifts, then land in clarity. Track yours.',
    examples: [
      '😶‍🌫️ Low & heavy (Tamas) — the "can\'t start" days',
      '⚡ Restless & busy (Rajas) — the "can\'t stop" days',
      '🌟 Calm & clear (Sattva) — the flow days',
    ],
    paragraphs: [
      "You already know the pattern. Some days you can't get out of bed. Other days you can't stop moving. And sometimes — rare and lovely — everything just flows. Vedic psychology named these three states the gunas, and noticed that everyone moves through all three. They're not personality types; they're closer to mental weather.",
      "Tamas is the gravitational pull of the unmoving — heaviness, fog, the wall between you and action. Rajas is heat — restlessness, ambition, the chase that never satisfies. Sattva is the clearing in the storm — light, present, things get done without force. Most growth shows up as an arc through them: stuck, then activated, then clear.",
      "Naming your story's arc helps readers find journeys that move the way theirs does. A 'rajas → sattva' story (burnout to clarity) is very different reading from 'tamas → rajas' (depression to activation), even when the surface details rhyme.",
    ],
    how_we_use:
      "Order matters. The arc is a trajectory — list the states in the order they appeared in your journey, not alphabetically.",
  },

  inner_qualities: {
    title: 'What shifted',
    sanskrit: 'Yogic qualities — virtues that ripen',
    summary:
      'The qualities that grew in you through the journey. Not character traits you were born with — these are virtues that ripen with practice and life: letting go, faith, patience, courage, generosity, contentment.',
    examples: [
      'Letting go — releasing what you used to grip',
      'Faith — trust before evidence',
      'Patience — letting things unfold in their time',
      'Courage — moving despite fear',
      'Generosity — open-handed living',
    ],
    paragraphs: [
      "What changed in you? Not the circumstances that changed around you — those are the plot of the story. What we want here is the inner shift: the quality that was missing before, that's a little more present now. The Yoga Sutras grouped these into yamas (restraints), niyamas (observances), and a wider set of yogic qualities — letting go, faith, courage, patience, generosity, contentment, surrender.",
      "These don't arrive fully formed. They ripen. Your story is partly the record of one of them ripening — sometimes by your effort, sometimes through grace, often both.",
    ],
    how_we_use:
      'Pick the one or two that genuinely shifted. If everything shifted, nothing shifted — be specific.',
  },

  inner_knots: {
    title: 'Inner knot',
    sanskrit: 'Kleshas — the five root patterns',
    summary:
      "Patanjali named five deep patterns that bind us: ignorance (not seeing things as they are), ego, attachment, aversion, and fear of change. Most struggles trace back to one of these. Naming the knot is the first thread to loosen it.",
    examples: [
      'Ignorance — mistaking the impermanent for permanent',
      'Ego / I-know-best — identification with self-image',
      'Attachment — clinging to pleasure, identity, the past',
      'Avoidance / Aversion — pushing away what feels hard',
      'Fear of change — clinging to the known',
    ],
    paragraphs: [
      "Two thousand years ago, Patanjali catalogued the five root patterns underneath nearly every human suffering. He called them kleshas — knots. Ignorance (avidya) is the master knot from which the others sprout: forgetting that we're more than our roles, our possessions, our story.",
      "From ignorance grow ego (asmita — taking the thinker for the self), attachment (raga — gripping pleasure), aversion (dvesha — pushing away pain), and fear of change (abhinivesha — clinging to the known, even to life itself). These aren't moral failings; they're built-in. The work isn't to eliminate them — it's to see them clearly enough that they stop running the show.",
      "Most transformation stories are about loosening one of these knots. Naming it gives readers a map: 'this is the pattern I'm working on too.'",
    ],
    how_we_use:
      'Pick the knot that was actually at the root of your struggle, even if multiple were active. The deepest one usually shows up in what you tried hardest to avoid feeling.',
  },

  obstacles: {
    title: 'Obstacle',
    sanskrit: 'Antarayas — the nine barriers (YS 1.30)',
    summary:
      "The nine barriers practitioners actually hit. Sutra 1.30 listed them with surprising specificity — illness, dullness, doubt, forgetting, laziness, sensory pull, wrong understanding, can't settle, can't sustain. If your story names what blocked the path, it helps others find theirs.",
    examples: [
      'Doubt — "is this even working?"',
      'Laziness / inertia — body resists action',
      "Can't settle — mind keeps slipping",
      "Can't stick — reaches the state but cannot stay",
      'Sensory pull — distraction, comfort-seeking',
    ],
    paragraphs: [
      "Sutra 1.30 of the Yoga Sutras is one of the most practically useful lines in spiritual literature. Patanjali could have written something lofty; instead he listed nine obstacles in the way of practice with the precision of a doctor listing symptoms. Illness. Dullness. Doubt. Forgetting. Laziness. Sensory pull. Wrong understanding. Can't settle. Can't sustain.",
      "Where the kleshas are root patterns, the antarayas are the day-to-day reasons we don't sit, don't show up, don't keep going. Naming the obstacle that actually got in your way is more useful than pretending you faced some heroic challenge.",
    ],
    how_we_use:
      "These are the practical blocks, not the deep psychological knot (that's inner_knots). 'I kept oversleeping' is an obstacle; 'I was avoiding the grief' is a knot.",
  },

  mental_states: {
    title: 'Mental state',
    sanskrit: 'Vrittis — the mind\'s movements',
    summary:
      'What the mind was doing during the struggle. Replaying the past, projecting the future, monkey-mind, fog, false perception — the Yoga Sutras catalog the modes of mental activity. Naming the mode helps you see it without becoming it.',
    examples: [
      'Replaying / regret — looping past events',
      'Worrying / planning — projecting forward',
      'Monkey mind — chaotic jumping',
      'Blank / fog — mental dullness',
      'Seeing it wrong — confidently believing untruth',
    ],
    paragraphs: [
      "Patanjali opened the Yoga Sutras with a definition: yoga is the stilling of the vrittis — the modulations of the mind. He then catalogued the modes the mind moves in: memory (smriti — replaying, regret, nostalgia, comparing), imagination (vikalpa — planning, worrying, monkey mind), sleep (nidra — drowsiness), fog (tamas — blank, boredom), and false perception (viparyaya — confidently believing what isn't so).",
      "These are not problems. They're the mind's natural motions. The work is to notice which one is running — 'oh, this is replaying again' — instead of being it. The simple act of naming creates distance.",
    ],
    how_we_use:
      "Pick the modes that dominated during the struggle, not the calm states you settled into afterward (those go in 'What shifted').",
  },

  practice_ids: {
    title: 'Practice',
    sanskrit: 'Sadhana — methods on the path',
    summary:
      "What you actually did. Meditation, asana, chanting, service, breathwork, study, therapy, classical arts — the methods that move inner work from idea to lived experience. There's no hierarchy here; what matters is what worked for you.",
    examples: [
      'Isha practices — Shambhavi, Isha Kriya, Yogasanas, chants',
      'Service — Karma yoga, Volunteering / Seva',
      'Vedic ritual — Sandhyavandanam, chanting',
      'Classical arts — dance, music as inner discipline',
      'Therapeutic — CBT, IFS, somatic work, journaling',
    ],
    paragraphs: [
      "Sadhana is the deliberate practice — the methods you actually used to do the inner work. The Indian traditions never settled on a single method; they offered hundreds, knowing that what wakes one person up bores another. Asana, pranayama, mantra, kriya, seva, swadhyaya, dhyana — and modern additions: somatic therapy, journaling, recovery programs, classical arts as devotion.",
      "Naming the practice in your story does two things. It credits the method honestly (this is what helped), and it lets readers find stories where someone tried what they're considering. A reader curious about Vipassana wants stories from people who actually sat 10 days, not vague references to 'meditation'.",
    ],
    how_we_use:
      "Tag what you actually practiced enough to matter — not every method you ever tried. If you mention something in passing, that's not a tag.",
  },
};
